import { useEffect, useState, useSyncExternalStore } from 'react';
import { CartaoApoio } from '../components/Recolhivel';
import { Alert, Modal, Pressable, ScrollView, Switch, Text, TextInput, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps, TopoProps } from './tipos';
import { dataBr, diasAte, din, dinDe, dinSomas, junta, menosSomas, num, op, somaPorMoeda, temSoma } from './util';
import { compartilharTexto } from '../lib/anexos';
import { whatsapp } from '../lib/mapa';
import { grupoDoRef, linkConvite, meuNome } from '../lib/grupos';
import { semBloqueio } from '../auth/AuthContext';
import { C, alpha } from '../components/theme';
import { N_, t } from '../lib/i18n';
import { addDays, horaTela, hm, norm, today, uid } from '../lib/date';
import { excluirReg, mudarCampos, mudarReg, novoReg, regsDe, salvarReg } from '../store/modulos';
import { Btn, Chip, Sheet } from '../components/ui';
import { CartaoTotal } from '../components/relatorio/Periodo';
import { RelatoriosGerados } from '../components/RelatoriosGerados';
import { marcarAbaAtual } from '../lib/relatorios';
import { dinheiro } from '../lib/moeda';
import { gerarECompartilharPdf, type BlocoPdf } from '../lib/pdf';
import { Barra, EditorOrcamento, gastoCat, mesmo, previstoCat, type CatOrc } from './viagensOrcamento';
import { membrosDaViagem, pessoasDaViagem, useMembrosViagem } from './viagens';

// ─── Texto 23: Eventos & Celebrações ─────────────────────────────────────────────────────────────
// Pessoas do evento (A), convidados com 5 situações (B), cronograma compartilhado (C), fornecedores com sinal e
// restante (D), orçamento e gastos com o ⊕ por convidado (E), extras (F) e relatório em PDF (G).
// O visual é o mesmo de Viagens (texto 19). Nada desta aba vai sozinho para o My Planner.

// B1: as 5 situações do convidado (os mesmos nomes no resumo, nos filtros, no cartão e no formulário)
const A_CONVIDAR = 'A convidar', SEM_RESPOSTA = 'Convidado, sem resposta';
const RSVP = [N_('A convidar'), N_('Convidado, sem resposta'), N_('Confirmado'), N_('Talvez'), N_('Não vai')];
/** Situação do convidado ("Pendente", das versões antigas, conta como "Convidado, sem resposta" se o convite já foi enviado). */
export const sitDe = (r: Reg): string => {
  const s = String(r.c.rsvp || '');
  if (!s || s === 'Pendente') return r.c.convidadoEm ? SEM_RESPOSTA : A_CONVIDAR;
  return s;
};
const ICONE_SIT: Record<string, string> = { 'A convidar': '📝', 'Convidado, sem resposta': '✉️', 'Confirmado': '✅', 'Talvez': '🤔', 'Não vai': '❌' };

/** Convite pronto para mandar pelo WhatsApp, e-mail ou outro app. */
export function convite(ev: Reg, nome?: string) {
  return [
    nome ? t('Olá, {nome}!', { nome }) : t('Olá!'),
    t('Você está convidado(a) para: {evento}', { evento: ev.c.nome || '' }),
    ev.c.data ? '📅 ' + dataBr(ev.c.data) + (ev.c.hora ? ' · ' + horaTela(ev.c.hora) : '') : '',
    ev.c.local ? '📍 ' + ev.c.local : '',
    ev.c.convite ? '\n' + ev.c.convite : '',
    '\n' + t('Pode confirmar sua presença respondendo esta mensagem?'),
    // evento com grupo: o convite já leva o link (a pessoa recebe a agenda e os avisos no celular)
    ...(() => { const g = grupoDoRef(ev.id); return g ? ['\n' + t('Para receber a agenda, o local e os avisos do evento no celular, entre no grupo: {link}', { link: linkConvite(g.codigo) })] : []; })(),
  ].filter(Boolean).join('\n');
}
const enviar = (txt: string, titulo: string) => semBloqueio(() => compartilharTexto(txt, titulo)).then(x => { if (x === 'copiado') Alert.alert(t('Texto copiado. Cole onde quiser enviar.')); }).catch(() => {});
/** B2: quem estava em "A convidar" passa sozinho para "Convidado, sem resposta" ao mandar o convite. */
function marcarConvidados(ctx: Ctx, ids: string[]) {
  for (const id of ids) {
    const r = ctx.porId(id);
    if (!r || r.col !== 'convidado') continue;
    const s = sitDe(r);
    mudarCampos(ctx.mod, r.id, { convidadoEm: r.c.convidadoEm || today(), ...(s === A_CONVIDAR ? { rsvp: SEM_RESPOSTA } : {}) });
  }
}

// ─── A: pessoas do evento (membros do grupo + nomes avulsos), como em Viagens ──────────────────────
export const pessoasDoEvento = (ev: Reg | null) => pessoasDaViagem(ev);

/** Quantas pessoas (contando os acompanhantes) em cada situação. */
export function contasEvento(ctx: Ctx, ev: Reg) {
  const conv = ctx.filhos(ev.id, 'convidado');
  const pessoas = (s: string) => conv.filter(r => sitDe(r) === s).reduce((n, r) => n + 1 + num(r.c.acomp), 0);
  const forn = ctx.filhos(ev.id, 'fornecedor');
  return { convidados: conv.length, pessoas: conv.reduce((n, r) => n + 1 + num(r.c.acomp), 0), aConvidar: pessoas(A_CONVIDAR), semResposta: pessoas(SEM_RESPOSTA), confirmados: pessoas('Confirmado'), talvez: pessoas('Talvez'), nao: pessoas('Não vai'),
    // 0.69 (B1): cada fornecedor pode ter a sua moeda; os totais ficam separados por moeda
    custo: somaPorMoeda(forn, r => num(r.c.valor), ctx.moeda), pago: somaPorMoeda(forn, r => pagoDoFornecedor(r), ctx.moeda), orcamento: num(ev.c.orcamento) };
}
/** E4: quantos convidados contam no ⊕ (confirmados + acompanhantes + extras de margem; os Talvez só com a chave ligada). */
export function convidadosQueContam(ctx: Ctx, ev: Reg) {
  const n = contasEvento(ctx, ev);
  const extras = Math.max(0, Math.round(num(ev.c.extras)));
  const talvez = ev.c.incluirTalvez ? n.talvez : 0;
  return { total: n.confirmados + talvez + extras, confirmados: n.confirmados, talvez, extras };
}

function PainelConvidados({ ctx, pai }: PainelProps) {
  if (!pai) return null;
  const n = contasEvento(ctx, pai);
  const Bloco = ({ v, l, cor }: { v: number; l: string; cor: string }) => (
    <View style={{ flexGrow: 1, flexBasis: '30%', backgroundColor: alpha(cor, 0.15), borderRadius: 12, padding: 8, alignItems: 'center' }}>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 18 }}>{v}</Text><Text style={{ color: C.sub, fontSize: 11, textAlign: 'center' }}>{l}</Text>
    </View>
  );
  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Bloco v={n.aConvidar} l={t('A convidar')} cor={C.sub} /><Bloco v={n.semResposta} l={t('Convidado, sem resposta')} cor={C.warn} />
        <Bloco v={n.confirmados} l={t('Confirmado')} cor={C.ok} /><Bloco v={n.talvez} l={t('Talvez')} cor={C.warn} /><Bloco v={n.nao} l={t('Não vai')} cor={C.danger} />
      </View>
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Contando os acompanhantes. Toque no status do convidado para mudar.')}</Text>
      <Pressable onPress={() => enviar(convite(pai), pai.c.nome)} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
        <Text style={{ color: '#fff', fontWeight: '800' }}>📨 {t('Enviar o convite')}</Text>
      </Pressable>
    </View>
  );
}

// ─── D: fornecedores (sinal e restante) ──────────────────────────────────────────────────────────
const temSinal = (c: Record<string, any>) => num(c.sinal) > 0;
const restanteDe = (c: Record<string, any>) => Math.max(0, Math.round((num(c.valor) - num(c.sinal)) * 100) / 100);
/** Quanto já foi pago ao fornecedor (o total, ou o sinal e o restante). */
function pagoDoFornecedor(r: Reg) {
  if (temSinal(r.c)) return (r.c.sinalPago ? num(r.c.sinal) : 0) + (r.c.restantePago ? restanteDe(r.c) : 0);
  return r.c.pago ? num(r.c.valorPago) || num(r.c.valor) : 0;
}
/** D4: o fornecedor entra como previsto quando já foi contratado (ou já tem algo pago). */
const contaNoPrevisto = (r: Reg) => !!r.c.contratado || pagoDoFornecedor(r) > 0;

/** D4/E6: o que foi pago ao fornecedor entra sozinho nos gastos do evento (sinal, restante ou o total), sem digitar de novo. */
function gastosDoFornecedor(ctx: Ctx, r: Reg) {
  if (!r.pai) return;
  const regs = regsDe(ctx.mod);
  const ja = regs.filter(g => g.col === 'gasto' && g.c.forn === r.id);
  const ev = regs.find(x => x.id === r.pai) ?? null;
  const nome = junta(r.c.servico, r.c.nome) || t('Fornecedor');
  const moeda = r.c.moedas?.valor;
  const partes: { parte: string; valor: number; data: string; desc: string }[] = [];
  if (temSinal(r.c)) {
    if (r.c.sinalPago) partes.push({ parte: 'sinal', valor: num(r.c.sinal), data: String(r.c.sinalData || '') || today(), desc: `${nome} · ${t('sinal')}` });
    if (r.c.restantePago && restanteDe(r.c) > 0) partes.push({ parte: 'restante', valor: restanteDe(r.c), data: String(r.c.restantePagoEm || r.c.restanteData || '') || today(), desc: `${nome} · ${t('restante')}` });
  } else if (r.c.pago && (num(r.c.valorPago) || num(r.c.valor)) > 0) {
    partes.push({ parte: 'total', valor: num(r.c.valorPago) || num(r.c.valor), data: String(r.c.pagoEm || '') || today(), desc: nome });
  }
  for (const p of partes) {
    const g = ja.find(x => x.c.parte === p.parte);
    const cat = String(r.c.cat || '') || 'Outros';
    if (g) {
      if (num(g.c.valor) !== p.valor || g.c.data !== p.data || g.c.cat !== cat || g.c.desc !== p.desc) mudarCampos(ctx.mod, g.id, { valor: p.valor, data: p.data, cat, desc: p.desc });
    } else salvarReg(ctx.mod, novoReg('gasto', r.pai, { desc: p.desc, valor: p.valor, data: p.data, cat, item: '', pago: true, pagou: pessoasDoEvento(ev)[0] ?? '',
      forn: r.id, parte: p.parte, ...(moeda ? { moedas: { valor: moeda } } : {}) }));
  }
  for (const g of ja) if (!partes.some(p => p.parte === g.c.parte)) excluirReg(ctx.mod, g.id);
}

function PainelFornecedores({ ctx, pai }: PainelProps) {
  if (!pai) return null;
  const n = contasEvento(ctx, pai);
  if (!temSoma(n.custo) && !n.orcamento) return null;
  const custo = n.custo.get(ctx.moeda) ?? 0;   // o orçamento é na moeda do app: compara só com o que está nela
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 4 }}>
      <Text style={{ color: C.text, fontWeight: '700' }}>💰 {t('Contratado: {v}', { v: dinSomas(n.custo, ctx.moeda) })}{n.orcamento ? ` / ${din(n.orcamento, ctx.moeda)}` : ''}</Text>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Pago: {p} · Falta pagar: {f}', { p: dinSomas(n.pago, ctx.moeda), f: dinSomas(menosSomas(n.custo, n.pago), ctx.moeda) })}</Text>
      {n.orcamento > 0 && custo > n.orcamento && <Text style={{ color: C.danger, fontSize: 12, fontWeight: '700' }}>⚠️ {t('Passou do orçamento em {v}', { v: din(custo - n.orcamento, ctx.moeda) })}</Text>}
    </View>
  );
}

// ─── E2: categorias prontas conforme o tipo do evento ──────────────────────────────────────────────
const cat = (id: string, nome: string, icone: string, linhas?: string[]): CatOrc => ({ id, nome, icone, ...(linhas ? { linhas: linhas.map((l, i) => ({ id: `${id}-${i}`, nome: l })) } : {}) });
const BASE = (): CatOrc[] => [
  cat('local', N_('Local / espaço'), '🏛️'),
  cat('comida', N_('Comida'), '🍽️', [N_('Buffet'), N_('Salgados'), N_('Doces'), N_('Bolo')]),
  cat('bebidas', N_('Bebidas'), '🥤'),
  cat('decoracao', N_('Decoração'), '🎈'),
  cat('lembrancinhas', N_('Lembrancinhas'), '🎁'),
  cat('convites', N_('Convites'), '✉️'),
  cat('musica', N_('Música / som / DJ'), '🎵'),
  cat('foto', N_('Foto e vídeo'), '📸'),
  cat('roupa', N_('Roupa e beleza'), '👗'),
  cat('alugueis', N_('Aluguéis (mesas, cadeiras, louças)'), '🪑'),
  cat('equipe', N_('Equipe (garçons, limpeza)'), '🧑‍🍳'),
];
const OUTROS = () => cat('outros', N_('Outros'), '💡');
const EXTRA_TIPO: Record<string, () => CatOrc[]> = {
  'Aniversário': () => [cat('recreacao', N_('Recreação e brinquedos'), '🤹')],
  'Casamento': () => [cat('cerimonia', N_('Cerimônia'), '⛪'), cat('aliancas', N_('Alianças'), '💍'), cat('celebrante', N_('Celebrante'), '🎙️')],
  'Chá de bebê': () => [cat('fraldas', N_('Fraldas'), '🧷'), cat('brincadeiras', N_('Brincadeiras'), '🎲')],
  'Formatura': () => [cat('beca', N_('Beca e cerimônia'), '🎓')],
  'Batizado': () => [cat('igreja', N_('Igreja e celebrante'), '⛪')],
  'Confraternização': () => [cat('brincadeiras', N_('Brincadeiras e amigo secreto'), '🎲')],
  'Corporativo': () => [cat('brindes', N_('Brindes'), '🎁'), cat('equipamentos', N_('Equipamentos (projetor, som)'), '📽️'), cat('transporte', N_('Transporte'), '🚌')],
};
const SEM_NO_TIPO: Record<string, string[]> = { 'Chá de bebê': ['musica'], 'Confraternização': ['roupa', 'convites'], 'Corporativo': ['lembrancinhas', 'roupa'] };
export const orcPadraoEvento = (tipo: string): CatOrc[] => [...BASE().filter(c => !(SEM_NO_TIPO[tipo] ?? []).includes(c.id)), ...(EXTRA_TIPO[tipo]?.() ?? []), OUTROS()];
export const orcDoEvento = (ev: Reg | null | undefined): CatOrc[] => (Array.isArray(ev?.c.orc) ? ev!.c.orc : orcPadraoEvento(String(ev?.c.tipo || '')));

/** D4: fornecedores contratados de uma categoria (o valor do contrato entra sozinho como previsto). */
const fornDaCat = (ctx: Ctx, ev: Reg, c: CatOrc) => ctx.filhos(ev.id, 'fornecedor').filter(f => contaNoPrevisto(f) && mesmo(f.c.cat, c.nome) && num(f.c.valor) > 0);
/** E1: os números do evento (na moeda do app). */
function numerosEvento(ctx: Ctx, ev: Reg) {
  const orc = orcDoEvento(ev);
  const nConv = convidadosQueContam(ctx, ev);
  const forn = ctx.filhos(ev.id, 'fornecedor').filter(f => contaNoPrevisto(f) && num(f.c.valor) > 0);
  const fornSemCat = forn.filter(f => !orc.some(c => mesmo(f.c.cat, c.nome)));
  const previsto = orc.reduce((s, c) => s + previstoCat(c, nConv.total) + fornDaCat(ctx, ev, c).reduce((s2, f) => s2 + num(f.c.valor), 0), 0)
    + fornSemCat.reduce((s, f) => s + num(f.c.valor), 0);
  const gastos = ctx.filhos(ev.id, 'gasto');
  const naMoeda = (g: Reg) => (g.c.moedas?.valor || ctx.moeda) === ctx.moeda;
  const gasto = gastos.filter(g => g.c.pago !== false && naMoeda(g)).reduce((s, g) => s + num(g.c.valor), 0);
  const faltaForn = forn.filter(f => (f.c.moedas?.valor || ctx.moeda) === ctx.moeda).reduce((s, f) => s + Math.max(0, num(f.c.valor) - pagoDoFornecedor(f)), 0);
  const faltaPagar = faltaForn + gastos.filter(g => g.c.pago === false && naMoeda(g)).reduce((s, g) => s + num(g.c.valor), 0);
  const orcamento = num(ev.c.orcamento);
  const base = orcamento || previsto;
  const ainda = base - gasto - faltaPagar;
  const porConvidado = nConv.total > 0 ? (previsto || gasto) / nConv.total : 0;
  return { orc, nConv, previsto, gasto, faltaPagar, orcamento, base, ainda, porConvidado, gastos };
}

function Numero({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return <View><Text style={{ color: C.sub, fontSize: 11 }}>{rotulo}</Text><Text style={{ color: cor ?? C.text, fontWeight: '800', fontSize: 16 }}>{valor}</Text></View>;
}
/** E1: números de cima (mesmo visual do Orçamento da viagem). */
function ResumoEvento({ ctx, ev }: { ctx: Ctx; ev: Reg }) {
  const x = numerosEvento(ctx, ev);
  const $ = (v: number) => din(v, ctx.moeda);
  return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.12), borderRadius: 14, padding: 12, gap: 6, borderWidth: 1, borderColor: alpha(ctx.cor, 0.35) }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>💰 {t('Orçamento do evento')}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        {x.orcamento > 0 && <Numero rotulo={t('Orçamento do evento')} valor={$(x.orcamento)} />}
        <Numero rotulo={t('Previsto')} valor={$(x.previsto)} />
        <Numero rotulo={t('Gasto')} valor={$(x.gasto)} />
        <Numero rotulo={t('Falta pagar')} valor={$(x.faltaPagar)} />
        {x.base > 0 && <Numero rotulo={x.ainda >= 0 ? t('Ainda pode gastar') : t('Passou do previsto')} valor={$(Math.abs(x.ainda))} cor={x.ainda >= 0 ? C.ok : C.danger} />}
      </View>
      <Barra gasto={x.gasto + x.faltaPagar} previsto={x.base} />
      {x.porConvidado > 0 && <Text style={{ color: C.sub, fontSize: 12 }}>👤 {t('{v} por convidado', { v: $(Math.round(x.porConvidado)) })}</Text>}
    </View>
  );
}

/** E4: quem conta no ⊕ por convidado: "10 confirmados + 3 extras = 13", extras de margem e "Incluir os Talvez". */
function ContaConvidados({ ctx, ev, pode }: { ctx: Ctx; ev: Reg; pode: boolean }) {
  const n = convidadosQueContam(ctx, ev);
  const partes = [n.confirmados === 1 ? t('1 confirmado') : t('{n} confirmados', { n: n.confirmados })];
  if (n.talvez) partes.push(n.talvez === 1 ? t('1 talvez') : t('{n} talvez', { n: n.talvez }));
  if (n.extras) partes.push(n.extras === 1 ? t('1 extra') : t('{n} extras', { n: n.extras }));
  const mudar = (d: number) => mudarCampos(ctx.mod, ev.id, { extras: Math.max(0, n.extras + d) });
  return (
    <CartaoApoio titulo={'👥 ' + t('Convidados que contam no ⊕')} chave={`eventos:${ev.id}:conta`} resumo={String(n.total)} gap={8}>
      <Text style={{ color: C.text }}>{partes.join(' + ')} = <Text style={{ fontWeight: '800' }}>{n.total}</Text></Text>
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Confirmados com os acompanhantes. Muda sozinho quando alguém confirma ou desconfirma.')}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ color: C.text, flex: 1 }}>{t('Extras de margem (quem aparece sem avisar)')}</Text>
        {pode && <Pressable onPress={() => mudar(-1)} hitSlop={8} accessibilityLabel={t('Menos um extra')} style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontWeight: '900', fontSize: 18 }}>−</Text></Pressable>}
        <Text style={{ color: C.text, fontWeight: '800', minWidth: 28, textAlign: 'center' }}>+{n.extras}</Text>
        {pode && <Pressable onPress={() => mudar(1)} hitSlop={8} accessibilityLabel={t('Mais um extra')} style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontWeight: '900', fontSize: 18 }}>+</Text></Pressable>}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ color: C.text, flex: 1 }}>{t('Incluir os Talvez')}</Text>
        <Switch disabled={!pode} value={!!ev.c.incluirTalvez} trackColor={{ true: ctx.cor }} onValueChange={v => mudarCampos(ctx.mod, ev.id, { incluirTalvez: v })} accessibilityLabel={t('Incluir os Talvez')} />
      </View>
    </CartaoApoio>
  );
}

// ─── E5: calculadora de festa (estimativa) ────────────────────────────────────────────────────────
type Sugestao = { nome: string; qtd: number; un: string; marcado: boolean };
const SUGESTOES: Record<string, () => Sugestao[]> = {
  comida: () => [{ nome: N_('Salgados'), qtd: 12, un: N_('un.'), marcado: true }, { nome: N_('Doces'), qtd: 4, un: N_('un.'), marcado: true }, { nome: N_('Bolo'), qtd: 0.1, un: N_('kg'), marcado: true }],
  bebidas: () => [{ nome: N_('Refrigerante'), qtd: 0.6, un: N_('L'), marcado: true }, { nome: N_('Água'), qtd: 0.5, un: N_('L'), marcado: true }, { nome: N_('Suco'), qtd: 0.3, un: N_('L'), marcado: true },
    { nome: N_('Cerveja (adultos)'), qtd: 1, un: N_('L'), marcado: false }],
};
const qtdTxt = (q: number) => String(Math.round(q * 100) / 100).replace('.', ',');
function Calculadora({ ctx, ev, catId, n, onFechar }: { ctx: Ctx; ev: Reg; catId: string; n: number; onFechar: () => void }) {
  const [l, setL] = useState<(Sugestao & { txt: string })[]>(() => (SUGESTOES[catId]?.() ?? []).map(s => ({ ...s, txt: qtdTxt(s.qtd) })));
  const orc = orcDoEvento(ev);
  const c = orc.find(x => x.id === catId);
  const levar = () => {
    if (!c) return onFechar();
    const escolhidas = l.filter(s => s.marcado && num(s.txt.replace(',', '.')) > 0);
    const linhas = [...(c.linhas ?? [])];
    for (const s of escolhidas) {
      const q = Math.round(num(s.txt.replace(',', '.')) * 1000) / 1000;
      const i = linhas.findIndex(y => mesmo(y.nome, s.nome) || mesmo(t(y.nome), t(s.nome)));
      if (i >= 0) linhas[i] = { ...linhas[i], qtdConv: q, un: s.un };
      else linhas.push({ id: uid(), nome: s.nome, qtdConv: q, un: s.un });
    }
    mudarCampos(ctx.mod, ev.id, { orc: orc.map(x => (x.id === catId ? { ...x, valor: undefined, linhas } : x)) });
    onFechar();
  };
  return (
    <Modal transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 20 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 10, alignSelf: 'center', width: '100%', maxWidth: 420 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>🧮 {t('Calcular quantidades')} · {t(c?.nome ?? '')}</Text>
          <Text style={{ color: C.warn, fontSize: 12, fontWeight: '700' }}>{t('Estimativa para {n} convidados. Mude o que quiser antes de levar.', { n })}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Quantidade por convidado')} → {t('total')}</Text>
          {l.map((s, i) => (
            <View key={s.nome} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Pressable onPress={() => setL(x => x.map((y, j) => (j === i ? { ...y, marcado: !y.marcado } : y)))} hitSlop={6} accessibilityRole="checkbox" accessibilityState={{ checked: s.marcado }}>
                <Text style={{ fontSize: 20, color: s.marcado ? ctx.cor : C.sub }}>{s.marcado ? '☑' : '☐'}</Text>
              </Pressable>
              <Text style={{ color: C.text, flex: 1 }} numberOfLines={1}>{t(s.nome)}</Text>
              <TextInput value={s.txt} onChangeText={v => setL(x => x.map((y, j) => (j === i ? { ...y, txt: v } : y)))} keyboardType="decimal-pad" inputAccessoryViewButtonLabel={t('Concluído')}
                accessibilityLabel={t('Quantidade por convidado')} style={{ width: 60, backgroundColor: C.card2, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 6, color: C.text, textAlign: 'right', fontWeight: '700' }} />
              <Text style={{ color: C.sub, fontSize: 12, width: 30 }}>{t(s.un)}</Text>
              <Text style={{ color: C.text, fontWeight: '800', width: 70, textAlign: 'right' }}>≈ {qtdTxt(num(s.txt.replace(',', '.')) * n)} {t(s.un)}</Text>
            </View>
          ))}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Os itens marcados entram na categoria com o ⊕ por convidado. Depois é só tocar no ⊕ e pôr o valor unitário.')}</Text>
          <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
            <Pressable onPress={onFechar} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Cancelar')}</Text></Pressable>
            <Pressable onPress={levar} disabled={!l.some(s => s.marcado)} hitSlop={8} style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 10, backgroundColor: l.some(s => s.marcado) ? ctx.cor : C.card2 }}>
              <Text style={{ color: '#fff', fontWeight: '800' }}>{t('Levar para os itens')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** E: sub-aba "Orçamento e gastos" do evento (o mesmo jeito da de Viagens). */
function PainelOrcamentoEvento(p: PainelProps) {
  const { ctx, pai, lista } = p;
  const [calc, setCalc] = useState<string | null>(null);
  if (!pai) return null;
  const pode = p.pode !== false;
  const nConv = convidadosQueContam(ctx, pai);
  return (
    <View style={{ gap: 10 }}>
      <ResumoEvento ctx={ctx} ev={pai} />
      <ContaConvidados ctx={ctx} ev={pai} pode={pode} />
      <CartaoApoio titulo={'🧾 ' + t('Orçamento por categoria')} chave={`eventos:${pai.id}:orcCat`} gap={10}
        resumo={t('Previsto {p} · gasto {g}', { p: din(numerosEvento(ctx, pai).previsto, ctx.moeda), g: din(numerosEvento(ctx, pai).gasto, ctx.moeda) })}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Coloque quanto pretende gastar em cada categoria. Use o ⊕ para calcular por convidado. Os fornecedores contratados entram sozinhos na categoria escolhida.')}</Text>
          <EditorOrcamento alvo={pai} gastos={lista} ctx={ctx} pode={pode} conv={{
            n: nConv.total, padrao: () => orcPadraoEvento(String(pai.c.tipo || '')),
            fixos: c => fornDaCat(ctx, pai, c).map(f => ({ nome: junta(f.c.servico, f.c.nome) || t('Fornecedor'), valor: num(f.c.valor) })),
            calcular: c => (SUGESTOES[c.id] ? () => setCalc(c.id) : null) }} />
      </CartaoApoio>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Chip small cor={ctx.cor} on={false} label={'📄 ' + t('Relatório do evento (PDF)')} onPress={() => abrirRelatorioEvento(pai.id)} />
      </View>
      {!!calc && <Calculadora ctx={ctx} ev={pai} catId={calc} n={nConv.total} onFechar={() => setCalc(null)} />}
    </View>
  );
}

const categoriasEvento = (ev: Reg | null, c?: Record<string, any>) => {
  const nomes = orcDoEvento(ev).map(x => x.nome);
  if (c?.cat) nomes.push(c.cat);
  return [...new Map(nomes.map(n => [norm(n), n])).values()];
};
function itensDaCategoria(ev: Reg | null, c?: Record<string, any>) {
  if (!c?.cat) return [];
  const linhas = orcDoEvento(ev).filter(x => mesmo(x.nome, String(c.cat))).flatMap(x => x.linhas ?? []).map(l => l.nome);
  if (c.item) linhas.push(c.item);
  return [...new Map(linhas.map(n => [norm(n), n])).values()];
}

// ─── C: cronograma do dia compartilhado ─────────────────────────────────────────────────────────
const SIT_CRON = [N_('A fazer'), N_('Em andamento'), N_('Feito')];
const quemDe = (r: Reg): string[] => (Array.isArray(r.c.quem) ? r.c.quem.map(String) : String(r.c.quem ?? '').trim() ? [String(r.c.quem).trim()] : []);
let meuNomeCache = '';
const ouvNome = new Set<() => void>();
function useMeuNome() {
  const n = useSyncExternalStore(cb => { ouvNome.add(cb); return () => { ouvNome.delete(cb); }; }, () => meuNomeCache, () => meuNomeCache);
  useEffect(() => { if (!meuNomeCache) meuNome().then(x => { meuNomeCache = x; ouvNome.forEach(f => f()); }).catch(() => {}); }, []);
  return n;
}
/** C6: no dia do evento, "Agora" (o último que já começou) e "Próximo" (o primeiro que ainda vai começar). */
function agoraEProximo(ctx: Ctx, ev: Reg | null, agora = hm(new Date())) {
  if (!ev || ev.c.data !== today()) return { agora: null as string | null, proximo: null as string | null };
  const l = ctx.filhos(ev.id, 'cronograma').filter(r => !!r.c.hora).sort((a, b) => String(a.c.hora).localeCompare(String(b.c.hora)));
  const jaComecou = l.filter(r => String(r.c.hora) <= agora);
  return { agora: jaComecou[jaComecou.length - 1]?.id ?? null, proximo: l.find(r => String(r.c.hora) > agora)?.id ?? null };
}
function useMinuto() {
  const [, set] = useState(0);
  useEffect(() => { const i = setInterval(() => set(x => x + 1), 60000); return () => clearInterval(i); }, []);
}
/** C4/C6: no cartão do momento: quem é responsável, o "🙋 Eu faço" e o destaque Agora / Próximo. */
function ExtraMomento({ r, ctx, pode }: { r: Reg; ctx: Ctx; pode: boolean }) {
  useMinuto();
  const eu = useMeuNome();
  const ev = ctx.porId(r.pai);
  const quem = quemDe(r);
  const { agora, proximo } = agoraEProximo(ctx, ev);
  const souEu = !!eu && quem.some(q => norm(q) === norm(eu));
  const podePegar = pode && !!ev?.g && !!eu && !souEu && r.c.sit !== 'Feito';
  if (!quem.length && !podePegar && agora !== r.id && proximo !== r.id) return null;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 2 }}>
      {agora === r.id && <Text style={{ color: '#fff', backgroundColor: ctx.cor, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2, fontSize: 11, fontWeight: '800', overflow: 'hidden' }}>▶ {t('Agora')}</Text>}
      {proximo === r.id && <Text style={{ color: ctx.cor, backgroundColor: alpha(ctx.cor, 0.15), borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2, fontSize: 11, fontWeight: '800', overflow: 'hidden' }}>⏭ {t('Próximo')}</Text>}
      {!!quem.length && <Text style={{ color: C.text, fontSize: 12, fontWeight: '700' }}>👤 {quem.join(', ')}</Text>}
      {podePegar && (
        <Pressable onPress={() => mudarReg(ctx.mod, r.id, x => ({ ...x, c: { ...x.c, quem: [...quemDe(x), eu] } }))} hitSlop={6} accessibilityRole="button"
          style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: alpha(ctx.cor, 0.18) }}>
          <Text style={{ color: ctx.cor, fontWeight: '800', fontSize: 12 }}>🙋 {t('Eu faço')}</Text>
        </Pressable>
      )}
    </View>
  );
}
/** C6: no dia do evento, um aviso no alto do cronograma com o que está acontecendo agora e o próximo. */
function TopoCronograma({ ctx, pai }: PainelProps) {
  useMinuto();
  const { agora, proximo } = agoraEProximo(ctx, pai);
  if (!pai || pai.c.data !== today()) return null;
  const nome = (id: string | null) => { const r = ctx.porId(id); return r ? junta(horaTela(r.c.hora), r.c.atividade) : ''; };
  return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.14), borderRadius: 14, padding: 12, gap: 4, borderWidth: 1, borderColor: alpha(ctx.cor, 0.4) }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>🎉 {t('Hoje é o dia do evento!')}</Text>
      {!!agora && <Text style={{ color: C.text }}>▶ {t('Agora')}: {nome(agora)}</Text>}
      {!!proximo && <Text style={{ color: C.sub }}>⏭ {t('Próximo')}: {nome(proximo)}</Text>}
    </View>
  );
}

// ─── F1: checklist de preparação por prazo ──────────────────────────────────────────────────────
const PREPARO: Record<string, [number, string][]> = {
  'Aniversário': [[60, N_('Reservar o local')], [45, N_('Contratar a comida e o bolo')], [30, N_('Enviar os convites')], [30, N_('Contratar a decoração')],
    [15, N_('Comprar as lembrancinhas')], [7, N_('Confirmar os fornecedores')], [7, N_('Confirmar a presença dos convidados')], [2, N_('Comprar bebidas e gelo')], [1, N_('Separar a roupa e o que levar')]],
  'Casamento': [[365, N_('Definir a data e o orçamento')], [300, N_('Reservar o local da cerimônia e da festa')], [270, N_('Contratar o buffet')], [240, N_('Contratar foto e vídeo')],
    [180, N_('Escolher a roupa dos noivos')], [120, N_('Avisar a data (save the date)')], [90, N_('Comprar as alianças')], [60, N_('Enviar os convites')], [30, N_('Prova final da roupa')],
    [15, N_('Confirmar a presença dos convidados')], [7, N_('Confirmar os fornecedores')], [1, N_('Separar os documentos e as alianças')]],
  'Chá de bebê': [[45, N_('Fazer a lista de presentes')], [30, N_('Enviar os convites')], [21, N_('Reservar o local')], [15, N_('Preparar as brincadeiras')],
    [7, N_('Comprar a decoração e as lembrancinhas')], [3, N_('Confirmar a presença dos convidados')], [1, N_('Comprar comida e bebidas')]],
  'Formatura': [[180, N_('Reservar o local ou a mesa da festa')], [120, N_('Comprar ou alugar a roupa')], [60, N_('Contratar foto e vídeo')], [30, N_('Enviar os convites')],
    [7, N_('Confirmar a presença dos convidados')], [1, N_('Separar os documentos e os ingressos')]],
  'Batizado': [[90, N_('Escolher os padrinhos')], [60, N_('Agendar na igreja')], [45, N_('Fazer o curso de batismo')], [30, N_('Enviar os convites')], [30, N_('Escolher a roupa')],
    [15, N_('Contratar a comida')], [7, N_('Confirmar os fornecedores e os convidados')]],
};
const PREPARO_PADRAO: [number, string][] = [[90, N_('Reservar o local')], [60, N_('Definir a lista de convidados')], [30, N_('Enviar os convites')], [30, N_('Contratar comida e bebidas')],
  [7, N_('Confirmar os fornecedores')], [3, N_('Confirmar a presença dos convidados')], [1, N_('Comprar o que falta')]];
const preparoDoTipo = (tipo: string) => PREPARO[tipo] ?? PREPARO_PADRAO;
const limiteDe = (r: Reg, ev: Reg | null) => (ev?.c.data && num(r.c.dias) >= 0 ? addDays(String(ev.c.data), -Math.round(num(r.c.dias))) : '');
const faltamTxt = (dias: number) => (dias === 0 ? t('No dia do evento') : dias === 1 ? t('Falta 1 dia') : t('Faltam {n} dias', { n: dias }));
function semearPreparo(ev: Reg): Reg[] {
  return preparoDoTipo(String(ev.c.tipo || '')).map(([d, tarefa], i) => ({ ...novoReg('preparo', ev.id, { tarefa: t(tarefa), dias: d, feito: false }), ordem: i }));
}
function TopoPreparo({ ctx, pai, lista, pode }: PainelProps) {
  if (!pai) return null;
  if (!pai.c.data) return <Text style={{ color: C.warn, fontSize: 12 }}>📅 {t('Coloque a data do evento (edite o evento) para ver até quando fazer cada item.')}</Text>;
  if (lista.length || pode === false) return null;
  return (
    <Pressable onPress={() => semearPreparo(pai).forEach(r => salvarReg(ctx.mod, r))} style={{ backgroundColor: alpha(ctx.cor, 0.14), borderRadius: 14, padding: 12, borderWidth: 1, borderColor: alpha(ctx.cor, 0.4) }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>✨ {t('Usar a lista pronta para {tipo}', { tipo: pai.c.tipo ? t(pai.c.tipo) : t('o evento') })}</Text>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Dá para mudar, tirar e incluir itens depois.')}</Text>
    </Pressable>
  );
}

// ─── F3: vaquinha ───────────────────────────────────────────────────────────────────────────────
function PainelVaquinha({ ctx, lista }: PainelProps) {
  if (!lista.length) return null;
  const comb = somaPorMoeda(lista, r => num(r.c.valor), ctx.moeda), deu = somaPorMoeda(lista, r => num(r.c.deu), ctx.moeda, 'deu');
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 4 }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>🤝 {t('Combinado: {v}', { v: dinSomas(comb, ctx.moeda) })}</Text>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Já deram: {d} · Falta: {f}', { d: dinSomas(deu, ctx.moeda), f: dinSomas(menosSomas(comb, deu), ctx.moeda) })}</Text>
    </View>
  );
}

// ─── Coleções ───────────────────────────────────────────────────────────────────────────────────
const evento: Colecao = {
  // texto 26 (B7, REGRAS 1.8): evento que já passou não se duplica (só Arquivar)
  semDuplicar: r => !!r.c.data && String(r.c.data) < today(),
  id: 'evento', nome: N_('Eventos'), um: N_('Evento'), icone: r => (r.c.tipo === 'Casamento' ? '💍' : r.c.tipo === 'Aniversário' ? '🎂' : r.c.tipo === 'Formatura' ? '🎓' : '🎉'),
  campos: [
    { k: 'nome', r: N_('Nome do evento'), t: 'texto', ph: N_('Ex.: Aniversário de 30 anos da Ana') },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Aniversário'), N_('Casamento'), N_('Chá de bebê'), N_('Formatura'), N_('Batizado'), N_('Confraternização'), N_('Corporativo'), N_('Outro')] },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [30, 7, 1] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'local', r: N_('Local'), t: 'endereco' },
    { k: 'orcamento', r: N_('Orçamento'), t: 'dinheiro' },
    // texto 23 (A): pessoas do evento (o grupo entra sozinho; "+ Adicionar pessoa" para quem não tem o app)
    { k: 'pessoas', r: N_('Pessoas do evento'), t: 'nomes', nomesAuto: (_ctx, reg) => membrosDaViagem(reg),
      dica: N_('Quem está no grupo do evento entra sozinho. Use "+ Adicionar pessoa" para quem não tem o app. Esta lista é usada no cronograma, nos gastos e na vaquinha.') },
    { k: 'convite', r: N_('Texto extra do convite (traje, presentes...)'), t: 'longo' },
  ],
  // texto 24 (6): plural certo; os dois números contam os acompanhantes
  titulo: r => r.c.nome || '', sub: (r, ctx) => { const n = contasEvento(ctx, r); return junta(horaTela(r.c.hora), r.c.local, n.pessoas > 0 && t('{c} confirmado(s) de {n} convidado(s)', { c: n.confirmados, n: n.pessoas })); },
  data: 'data', anexos: true, segExtra: { orc: 'fin' },
  agenda: r => ({ time: r.c.hora || null, notes: r.c.local || '' }),
  acoes: r => [{ label: '📨 ' + t('Enviar o convite'), onPress: () => enviar(convite(r), r.c.nome) },
    { label: '📄 ' + t('Relatório do evento (PDF)'), onPress: () => abrirRelatorioEvento(r.id) }],
  // F1: o checklist de preparação já vem pronto pelo tipo do evento
  semear: (_ctx, r) => semearPreparo(r),
  painelTopo: ({ ctx }: TopoProps) => <RelatorioEvento ctx={ctx} />,
  topoLista: ({ ctx }) => <RelatorioEvento ctx={ctx} />,
  vazio: N_('Crie o evento e, dentro dele, a lista de convidados com confirmação de presença, os fornecedores e o cronograma do dia.'),
};

const convidado: Colecao = {
  id: 'convidado', pai: 'evento', nome: N_('Convidados'), um: N_('Convidado'), icone: r => ICONE_SIT[sitDe(r)] ?? '✉️',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'grupo', r: N_('Grupo'), t: 'opcao', op: [N_('Família'), N_('Amigos'), N_('Trabalho'), N_('Outro')], outro: true },
    { k: 'rsvp', r: N_('Presença'), t: 'opcao', op: RSVP },
    { k: 'acomp', r: N_('Acompanhantes'), t: 'numero' },
    { k: 'notas', r: N_('Observações (restrição alimentar...)'), t: 'texto' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(op(r.c.grupo), num(r.c.acomp) && t('+{n} acompanhante(s)', { n: num(r.c.acomp) }), r.c.telefone),
  ordem: 'titulo', rapido: 'rsvp', novo: () => ({ rsvp: A_CONVIDAR, acomp: 0 }),
  filtros: RSVP.map(s => [s, r => sitDe(r) === s] as [string, (r: Reg) => boolean]), contarZero: true,
  pesoFiltro: r => 1 + num(r.c.acomp),   // B: os filtros contam como o resumo (com os acompanhantes)
  acoes: (r, ctx) => { const ev = ctx.porId(r.pai); return ev ? [
    ...(r.c.telefone ? [{ label: '💬 ' + t('Enviar convite pelo WhatsApp'), onPress: () => { marcarConvidados(ctx, [r.id]); whatsapp(r.c.telefone, convite(ev, r.c.nome)); } }] : []),
    { label: '📨 ' + t('Enviar convite para {nome}', { nome: r.c.nome }), onPress: () => { marcarConvidados(ctx, [r.id]); enviar(convite(ev, r.c.nome), ev.c.nome); } }] : []; },
  // B2: "Enviar o convite" para vários (seleção): quem estava em "A convidar" passa para "Convidado, sem resposta"
  acoesSelecao: (ctx, pai) => (pai ? [{ label: '📨 ' + t('Enviar o convite'), onPress: ids => { marcarConvidados(ctx, ids); enviar(convite(pai), pai.c.nome); } }] : []),
  painel: PainelConvidados,
};

const fornecedor: Colecao = {
  id: 'fornecedor', check: 'pago', pagamento: { tipo: 'pagar', venc: 'data', data: 'pagoEm', valorPago: 'valorPago' }, pai: 'evento', nome: N_('Fornecedores'), um: N_('Fornecedor'), icone: r => (r.c.pago ? '✅' : r.c.contratado ? '🤝' : '📋'),
  campos: [
    { k: 'servico', r: N_('Serviço'), t: 'texto', ph: N_('Ex.: Buffet, DJ, decoração, fotógrafo') },
    { k: 'nome', r: N_('Empresa ou pessoa'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'cat', r: N_('Categoria do orçamento'), t: 'opcao', traduzir: true, opDe: (_ctx, pai, c) => categoriasEvento(pai, c),
      dica: N_('O valor do contrato entra sozinho como previsto nesta categoria, e o que for pago entra nos gastos.') },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'contratado', r: N_('Contratado'), t: 'sim', interruptor: true },
    // D3: sinal e restante (opcionais)
    { k: 'sinal', r: N_('Sinal'), t: 'dinheiro', seg: 'fin' },
    { k: 'sinalData', r: N_('Data do sinal'), t: 'data', mostrar: c => temSinal(c), seg: 'fin' },
    { k: 'sinalPago', r: N_('Sinal pago'), t: 'sim', interruptor: true, mostrar: c => temSinal(c), seg: 'fin' },
    { k: 'restante', r: N_('Restante'), t: 'dinheiro', mostrar: c => temSinal(c), seg: 'fin', calculado: c => (temSinal(c) ? N_('Calculado sozinho: valor menos o sinal.') : null) },
    { k: 'restanteData', r: N_('Data do restante'), t: 'data', semHoje: true, mostrar: c => temSinal(c), seg: 'fin' },
    { k: 'restantePago', r: N_('Restante pago'), t: 'sim', interruptor: true, mostrar: c => temSinal(c), seg: 'fin', dataAoLigar: 'restantePagoEm', rotuloLigado: N_('Pago em {d}') },
    { k: 'pago', r: N_('Pago'), t: 'sim', interruptor: true, seg: 'fin' },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago && !temSinal(c), seg: 'fin' },
    { k: 'valorPago', r: N_('Valor pago'), t: 'dinheiro', mostrar: c => !!c.pago && !temSinal(c) },
    { k: 'data', r: N_('Prazo (pagamento ou entrega)'), t: 'data', aviso: [3], semHoje: true },
  ],
  titulo: r => r.c.servico || '',
  sub: (r, ctx) => {
    const v = (x: number) => din(x, r.c.moedas?.valor || ctx.moeda);
    const sit = r.c.pago ? '✓ ' + t('pago')
      : temSinal(r.c) && r.c.sinalPago ? junta(t('Sinal {v} pago', { v: v(num(r.c.sinal)) }), restanteDe(r.c) > 0 && (r.c.restanteData ? t('falta {v} até {d}', { v: v(restanteDe(r.c)), d: dataBr(r.c.restanteData).slice(0, 5) }) : t('falta {v}', { v: v(restanteDe(r.c)) })))
      : r.c.contratado ? t('contratado') : t('orçando');
    return junta(r.c.nome, r.c.valor !== '' && r.c.valor != null && dinDe(r, 'valor', ctx.moeda), sit);
  },
  // D3: o restante é calculado sozinho; pagar o sinal e o restante = pago; marcar "Pago" com sinal marca as duas partes
  ajustar: (c, mudou) => {
    const n = { ...c };
    if (mudou === 'valor' || mudou === 'sinal') n.restante = temSinal(n) ? restanteDe(n) : '';
    if (temSinal(n)) {
      if (mudou === 'pago') { n.sinalPago = !!n.pago; n.restantePago = !!n.pago; if (n.pago && !n.restantePagoEm) n.restantePagoEm = n.pagoEm || today(); }
      if (mudou === 'sinalPago' || mudou === 'restantePago') n.pago = !!n.sinalPago && !!n.restantePago;
      if (n.sinalPago && !n.sinalData) n.sinalData = today();
      if (n.sinalPago || n.restantePago) n.contratado = true;
    }
    if (mudou === 'pago' && n.pago) n.contratado = true;
    return n;
  },
  aoSalvar: (r, ctx) => gastosDoFornecedor(ctx, r),
  data: 'data', feito: r => !!r.c.pago, anexos: true, painel: PainelFornecedores,
};

const cronograma: Colecao = {
  id: 'cronograma', check: 'feito', pai: 'evento', nome: N_('Cronograma do dia'), um: N_('Momento do evento'), icone: r => (r.c.feito ? '✅' : r.c.sit === 'Em andamento' ? '🔄' : '⏱️'),
  campos: [
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'atividade', r: N_('O que acontece'), t: 'texto', ph: N_('Ex.: Entrada dos noivos') },
    // C3: o responsável é escolhido nas Pessoas do evento (com caixinhas)
    { k: 'quem', r: N_('Responsável'), t: 'multi', opDe: (_ctx, pai, c) => [...new Map([...pessoasDoEvento(pai), ...(Array.isArray(c?.quem) ? c!.quem : [])].map(x => [norm(String(x)), String(x)])).values()],
      semOpcoes: N_('Coloque as pessoas em "Pessoas do evento" (edite o evento).') },
    // C2: situação (o antigo "Feito: Não" virou esta situação; a caixinha do cartão marca Feito)
    { k: 'sit', r: N_('Situação'), t: 'opcao', op: SIT_CRON },
    { k: 'frase', r: N_('Frase curta (opcional)'), t: 'texto', ph: N_('Ex.: em negociação'), mostrar: c => c.sit === 'Em andamento' },
    { k: 'feito', r: N_('Feito'), t: 'sim', mostrar: () => false },
  ],
  titulo: r => junta(horaTela(r.c.hora), r.c.atividade),
  sub: r => (r.c.sit === 'Em andamento' && r.c.frase ? `“${r.c.frase}”` : ''),
  // C1: em ordem de horário (sem horário vai para o fim)
  ordem: (a, b) => String(a.c.hora || '99').localeCompare(String(b.c.hora || '99')),
  rapido: 'sit', novo: () => ({ sit: 'A fazer', feito: false, quem: [] }),
  aoRapido: (_r, novo) => (novo === 'Feito' ? { mudar: { feito: true } } : novo === 'Em andamento' ? { mudar: { feito: false }, abrir: true } : { mudar: { feito: false, frase: '' } }),
  ajustar: (c, mudou) => {
    if (mudou === 'feito') return { ...c, sit: c.feito ? 'Feito' : c.sit === 'Feito' || !c.sit ? 'A fazer' : c.sit };
    if (mudou === 'sit') return { ...c, feito: c.sit === 'Feito' };
    return c;
  },
  extraCartao: ExtraMomento, topoLista: TopoCronograma,
  filtros: [[N_('A fazer'), r => (r.c.sit || 'A fazer') === 'A fazer' && !r.c.feito], [N_('Em andamento'), r => r.c.sit === 'Em andamento' && !r.c.feito], [N_('Feito'), r => !!r.c.feito]], contarZero: true,
  agenda: (r, ctx) => ({ start: ctx.porId(r.pai)?.c.data, time: r.c.hora || null, title: r.c.atividade }),
};

// E: orçamento e gastos do evento
const gasto: Colecao = {
  id: 'gasto', pai: 'evento', nome: N_('Orçamento e gastos'), um: N_('Gasto'), icone: r => (r.c.forn ? '🤝' : r.c.compra ? '🛒' : '💳'),
  campos: [
    { k: 'desc', r: N_('Descrição'), t: 'texto' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'cat', r: N_('Categoria'), t: 'opcao', traduzir: true, opDe: (_ctx, pai, c) => categoriasEvento(pai, c) },
    { k: 'item', r: N_('Item da categoria'), t: 'opcao', traduzir: true, opDe: (_ctx, pai, c) => itensDaCategoria(pai, c), mostrar: (c, _ctx, pai) => itensDaCategoria(pai ?? null, c).length > 0 },
    { k: 'pagou', r: N_('Quem pagou'), t: 'opcao', opDe: (_c, pai) => pessoasDoEvento(pai), semOpcoes: N_('Coloque as pessoas em "Pessoas do evento" (edite o evento).') },
    { k: 'pago', r: N_('Já está pago'), t: 'sim', interruptor: true, seg: 'fin' },
  ],
  titulo: r => r.c.desc || '',
  sub: (r, ctx) => junta(dinDe(r, 'valor', ctx.moeda), junta(op(r.c.cat), op(r.c.item)).replace(' · ', ' › '), r.c.pagou && t('pago por {p}', { p: r.c.pagou }), r.c.pago === false && t('a pagar')),
  data: 'data', ordem: 'data-desc', anexos: true, moedaPorValor: true,
  novo: (_ctx, pai) => ({ data: today(), pago: true, pagou: pessoasDoEvento(pai)[0] ?? '' }),
  ajustar: (c, mudou) => (mudou === 'cat' ? { ...c, item: '' } : c),
  filtros: [[N_('A pagar'), r => r.c.pago === false], [N_('Pagos'), r => r.c.pago !== false]],
  filtroArquivados: true, semEnviarLista: true,
  painel: PainelOrcamentoEvento,
  vazio: N_('Lance aqui o que foi gasto. Os pagamentos aos fornecedores e as compras marcadas entram sozinhos.'),
};

// E7: lista de compras do evento
function gastoDaCompra(ctx: Ctx, r: Reg, valor: number | null) {
  const ja = regsDe(ctx.mod).find(g => g.col === 'gasto' && g.c.compra === r.id);
  if (!r.c.comprado) { if (ja) excluirReg(ctx.mod, ja.id); return; }
  if (valor == null) return;
  const ev = ctx.porId(r.pai);
  if (ja) { mudarCampos(ctx.mod, ja.id, { valor, data: today() }); return; }
  salvarReg(ctx.mod, novoReg('gasto', r.pai, { desc: junta(r.c.nome, r.c.qtd) || t('Compra'), valor, data: today(), cat: String(r.c.cat || '') || 'Outros', item: '',
    pago: true, pagou: pessoasDoEvento(ev)[0] ?? '', compra: r.id }));
}
const compra: Colecao = {
  id: 'compra', check: 'comprado', pai: 'evento', nome: N_('Lista de compras'), um: N_('Item da lista de compras'), icone: r => (r.c.comprado ? '✅' : '🛒'),
  campos: [
    { k: 'nome', r: N_('O que comprar'), t: 'texto', ph: N_('Ex.: Copos descartáveis') },
    { k: 'qtd', r: N_('Quantidade'), t: 'texto', ph: N_('Ex.: 200 un.') },
    { k: 'onde', r: N_('Onde comprar'), t: 'texto', sugestoes: true },
    { k: 'cat', r: N_('Categoria do orçamento'), t: 'opcao', traduzir: true, opDe: (_ctx, pai, c) => categoriasEvento(pai, c) },
    { k: 'comprado', r: N_('Comprado'), t: 'sim', interruptor: true },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(r.c.qtd, r.c.onde && t('em {l}', { l: r.c.onde }), op(r.c.cat)),
  feito: r => !!r.c.comprado,
  filtros: [[N_('Falta comprar'), r => !r.c.comprado], [N_('Comprados'), r => !!r.c.comprado]], contarZero: true,
  aoMarcar: { titulo: N_('Quanto custou?'), dica: N_('O valor entra nos gastos do evento, na categoria do item. Se preferir, pule.'), ok: (r, ctx, v) => gastoDaCompra(ctx, r, v) },
  aoSalvar: (r, ctx) => gastoDaCompra(ctx, r, null),
};

const preparo: Colecao = {
  id: 'preparo', check: 'feito', pai: 'evento', nome: N_('Preparação'), um: N_('Item da preparação'), icone: r => (r.c.feito ? '✅' : '📋'),
  campos: [
    { k: 'tarefa', r: N_('O que fazer'), t: 'texto', ph: N_('Ex.: Reservar o local') },
    { k: 'dias', r: N_('Quantos dias antes do evento'), t: 'numero', un: N_('dias') },
    { k: 'feito', r: N_('Feito'), t: 'sim', interruptor: true },
  ],
  titulo: r => r.c.tarefa || '',
  sub: (r, ctx) => {
    const ev = ctx.porId(r.pai), lim = limiteDe(r, ev);
    const dias = Math.round(num(r.c.dias));
    if (!lim) return faltamTxt(dias);
    const resta = diasAte(lim);
    return junta(faltamTxt(dias), t('até {d}', { d: dataBr(lim) }), !r.c.feito && (resta < 0 ? '⚠️ ' + t('atrasado') : resta === 0 ? t('hoje') : ''));
  },
  ordem: (a, b) => num(b.c.dias) - num(a.c.dias),
  feito: r => !!r.c.feito, novo: () => ({ dias: 7, feito: false }),
  filtros: [[N_('Falta'), r => !r.c.feito], [N_('Feito'), r => !!r.c.feito]], contarZero: true,
  topoLista: TopoPreparo,
  agenda: (r, ctx) => { const lim = limiteDe(r, ctx.porId(r.pai)); return lim ? { start: lim, title: r.c.tarefa } : null; },
};

const presente: Colecao = {
  id: 'presente', check: 'agradeci', pai: 'evento', nome: N_('Presentes'), um: N_('Presente recebido'), icone: r => (r.c.agradeci ? '💌' : '🎁'),
  campos: [
    { k: 'quem', r: N_('Quem deu'), t: 'opcao', opDe: (ctx, pai) => (pai ? ctx.filhos(pai.id, 'convidado').map(x => String(x.c.nome || '')).filter(Boolean) : []), outro: true,
      semOpcoes: N_('Escolha "Outro" para escrever o nome.') },
    { k: 'oque', r: N_('O quê'), t: 'texto', ph: N_('Ex.: Jogo de panelas') },
    { k: 'agradeci', r: N_('Agradeci'), t: 'sim', interruptor: true, dataAoLigar: 'agradeciEm', rotuloLigado: N_('Agradeci em {d}') },
  ],
  titulo: r => r.c.oque || r.c.quem || '', sub: r => junta(r.c.quem && t('de {p}', { p: r.c.quem }), r.c.agradeci && '✓ ' + t('agradeci')),
  feito: r => !!r.c.agradeci,
  filtros: [[N_('Falta agradecer'), r => !r.c.agradeci], [N_('Agradecidos'), r => !!r.c.agradeci]], contarZero: true,
};

const cota: Colecao = {
  id: 'cota', pai: 'evento', nome: N_('Vaquinha'), um: N_('Parte da vaquinha'), icone: r => (num(r.c.deu) >= num(r.c.valor) && num(r.c.valor) > 0 ? '✅' : '🤝'),
  campos: [
    { k: 'pessoa', r: N_('Pessoa'), t: 'opcao', opDe: (_ctx, pai) => pessoasDoEvento(pai), semOpcoes: N_('Coloque as pessoas em "Pessoas do evento" (edite o evento).') },
    { k: 'valor', r: N_('Quanto vai contribuir'), t: 'dinheiro' },
    { k: 'deu', r: N_('Quanto já deu'), t: 'dinheiro' },
  ],
  titulo: r => r.c.pessoa || '',
  sub: (r, ctx) => { const m = r.c.moedas?.valor || ctx.moeda; const f = num(r.c.valor) - num(r.c.deu);
    return junta(t('deu {d} de {v}', { d: din(num(r.c.deu), m), v: din(num(r.c.valor), m) }), f > 0.004 ? t('falta {v}', { v: din(f, m) }) : '✓ ' + t('completo')); },
  feito: r => num(r.c.valor) > 0 && num(r.c.deu) >= num(r.c.valor),
  // F3: com 1 pessoa só, a vaquinha não aparece
  mostrarSe: (_ctx, pai) => pessoasDoEvento(pai).length > 1,
  painel: PainelVaquinha,
};

export const EVENTOS: ModDef = { id: 'eventos', colecoes: [evento, convidado, fornecedor, cronograma, gasto, compra, preparo, presente, cota],
  // B5 / C2-C3: valores antigos. "Pendente" vira "A convidar" (as versões antigas não guardavam se o convite já tinha sido
  // enviado); o responsável escrito vira a lista de pessoas; o "Feito" vira a situação.
  migrar: regs => {
    const out: { id: string; c: Record<string, any> }[] = [];
    let pend = 0;
    for (const r of regs) {
      if (r.col === 'convidado' && (r.c.rsvp === 'Pendente' || !r.c.rsvp)) { out.push({ id: r.id, c: { rsvp: r.c.convidadoEm ? SEM_RESPOSTA : A_CONVIDAR } }); pend++; }
      if (r.col === 'cronograma') {
        const m: Record<string, any> = {};
        if (!Array.isArray(r.c.quem)) m.quem = quemDe(r);
        if (!r.c.sit) m.sit = r.c.feito ? 'Feito' : 'A fazer';
        if (Object.keys(m).length) out.push({ id: r.id, c: m });
      }
    }
    if (pend) setTimeout(() => Alert.alert(t('Convidados'), pend === 1
      ? t('1 convidado que estava em "Pendente" passou para "A convidar". Ao enviar o convite pelo app, ele passa sozinho para "Convidado, sem resposta".')
      : t('{n} convidados que estavam em "Pendente" passaram para "A convidar". Ao enviar o convite pelo app, eles passam sozinhos para "Convidado, sem resposta".', { n: pend })), 800);
    return out;
  } };

// ─── G: relatório do evento em PDF ──────────────────────────────────────────────────────────────
const PARTES_REL = [['resumo', N_('Resumo do evento')], ['convidados', N_('Convidados (por situação, com acompanhantes)')], ['fornecedores', N_('Fornecedores (contratado, pago, falta)')],
  ['orcamento', N_('Orçamento previsto × gasto por categoria')], ['gastos', N_('Gastos')], ['compras', N_('Lista de compras')], ['cronograma', N_('Cronograma do dia (com responsáveis e situação)')],
  ['preparo', N_('Checklist de preparação')], ['presentes', N_('Presentes')], ['vaquinha', N_('Vaquinha')], ['notas', N_('Anotações')]] as const;
type ParteRel = typeof PARTES_REL[number][0];
const PADRAO_REL: ParteRel[] = PARTES_REL.map(([k]) => k);

async function relatorioEventoPdf(ctx: Ctx, ev: Reg, sel: ParteRel[], telefones: boolean) {
  const tem = (p: ParteRel) => sel.includes(p);
  const $ = (v: number) => dinheiro(v, ctx.moeda);
  const x = numerosEvento(ctx, ev);
  const f = (col: string) => ctx.filhos(ev.id, col);
  const b: BlocoPdf[] = [{ t: 'titulo', texto: ev.c.nome || t('Evento') }];
  if (tem('resumo')) {
    b.push({ t: 'par', texto: junta(op(ev.c.tipo), ev.c.data && dataBr(ev.c.data), horaTela(ev.c.hora), ev.c.local) });
    const pessoas = pessoasDoEvento(ev);
    if (pessoas.length) b.push({ t: 'par', texto: t('Pessoas do evento: {p}', { p: pessoas.join(', ') }) });
    const n = contasEvento(ctx, ev);
    b.push({ t: 'par', texto: RSVP.map(s => `${t(s)}: ${s === A_CONVIDAR ? n.aConvidar : s === SEM_RESPOSTA ? n.semResposta : s === 'Confirmado' ? n.confirmados : s === 'Talvez' ? n.talvez : n.nao}`).join(' · ') });
    b.push({ t: 'tabela', colunas: [t('Orçamento do evento'), t('Previsto'), t('Gasto'), t('Falta pagar'), x.ainda >= 0 ? t('Ainda pode gastar') : t('Passou do previsto')],
      linhas: [[x.orcamento ? $(x.orcamento) : '', $(x.previsto), $(x.gasto), $(x.faltaPagar), $(Math.abs(x.ainda))]] });
    if (x.porConvidado > 0) b.push({ t: 'par', texto: t('{v} por convidado', { v: $(Math.round(x.porConvidado)) }) });
  }
  const conv = f('convidado');
  if (tem('convidados') && conv.length) {
    b.push({ t: 'sub', texto: t('Convidados') });
    for (const s of RSVP) {
      const l = conv.filter(r => sitDe(r) === s).sort((a, c) => String(a.c.nome).localeCompare(String(c.c.nome)));
      if (!l.length) continue;
      b.push({ t: 'par', negrito: true, texto: `${t(s)} (${l.reduce((n, r) => n + 1 + num(r.c.acomp), 0)})` },
        { t: 'tabela', colunas: telefones ? [t('Nome'), t('Grupo'), t('Acompanhantes'), t('Telefone')] : [t('Nome'), t('Grupo'), t('Acompanhantes')],
          larguras: telefones ? [0.4, 0.2, 0.15, 0.25] : [0.5, 0.3, 0.2],
          linhas: l.map(r => [r.c.nome || '', op(r.c.grupo), num(r.c.acomp) ? String(num(r.c.acomp)) : '', ...(telefones ? [r.c.telefone || ''] : [])]) });
    }
  }
  const forn = f('fornecedor');
  if (tem('fornecedores') && forn.length) b.push({ t: 'sub', texto: t('Fornecedores') },
    { t: 'tabela', colunas: [t('Serviço'), t('Empresa ou pessoa'), t('Valor'), t('Pago'), t('Falta'), t('Situação')], larguras: [0.2, 0.24, 0.14, 0.14, 0.14, 0.14],
      linhas: forn.map(r => { const m = r.c.moedas?.valor || ctx.moeda; return [r.c.servico || '', r.c.nome || '', num(r.c.valor) ? din(num(r.c.valor), m) : '', din(pagoDoFornecedor(r), m),
        din(Math.max(0, num(r.c.valor) - pagoDoFornecedor(r)), m), r.c.pago ? t('pago') : r.c.contratado ? t('contratado') : t('orçando')]; }) });
  if (tem('orcamento')) {
    const linhas = x.orc.map(c => {
      const prev = previstoCat(c, x.nConv.total) + fornDaCat(ctx, ev, c).reduce((s, y) => s + num(y.c.valor), 0), g = gastoCat(x.gastos.filter(y => y.c.pago !== false), c, ctx.moeda);
      return prev || g ? [t(c.nome), $(prev), $(g), $(prev - g)] : null;
    }).filter(Boolean) as string[][];
    if (linhas.length) {
      linhas.push([t('Total'), $(x.previsto), $(x.gasto), $(x.previsto - x.gasto)]);
      b.push({ t: 'sub', texto: t('Orçamento') }, { t: 'par', texto: t('{n} convidados contam no ⊕ por convidado.', { n: x.nConv.total }) },
        { t: 'tabela', colunas: [t('Categoria'), t('Previsto'), t('Gasto'), t('Diferença')], larguras: [0.4, 0.2, 0.2, 0.2], linhas });
    }
  }
  if (tem('gastos') && x.gastos.length) b.push({ t: 'sub', texto: t('Gastos') },
    { t: 'tabela', colunas: [t('Data'), t('Descrição'), t('Categoria'), t('Pagou'), t('Valor')], larguras: [0.12, 0.34, 0.24, 0.14, 0.16],
      linhas: [...x.gastos].sort((a, c) => String(a.c.data).localeCompare(String(c.c.data))).map(g => [dataBr(g.c.data), junta(g.c.desc, g.c.pago === false && t('a pagar')), junta(op(g.c.cat), op(g.c.item)), g.c.pagou || '', dinDe(g, 'valor', ctx.moeda)]) });
  const compras = f('compra');
  if (tem('compras') && compras.length) b.push({ t: 'sub', texto: t('Lista de compras') },
    { t: 'tabela', colunas: [t('Item'), t('Quantidade'), t('Onde comprar'), t('Situação')], larguras: [0.36, 0.2, 0.24, 0.2],
      linhas: compras.map(r => [r.c.nome || '', r.c.qtd || '', r.c.onde || '', r.c.comprado ? t('Comprado') : t('Falta comprar')]) });
  const cron = f('cronograma').sort((a, c) => String(a.c.hora || '99').localeCompare(String(c.c.hora || '99')));
  if (tem('cronograma') && cron.length) b.push({ t: 'sub', texto: t('Cronograma do dia') },
    { t: 'tabela', colunas: [t('Horário'), t('O que acontece'), t('Responsável'), t('Situação')], larguras: [0.12, 0.4, 0.26, 0.22],
      linhas: cron.map(r => [horaTela(r.c.hora), r.c.atividade || '', quemDe(r).join(', '), junta(op(r.c.sit || (r.c.feito ? 'Feito' : 'A fazer')), r.c.sit === 'Em andamento' && r.c.frase)]) });
  const prep = f('preparo').sort((a, c) => num(c.c.dias) - num(a.c.dias));
  if (tem('preparo') && prep.length) b.push({ t: 'sub', texto: t('Checklist de preparação') },
    { t: 'tabela', colunas: [t('Até'), t('O que fazer'), t('Situação')], larguras: [0.2, 0.56, 0.24],
      linhas: prep.map(r => [dataBr(limiteDe(r, ev)) || faltamTxt(Math.round(num(r.c.dias))), r.c.tarefa || '', r.c.feito ? t('Feito') : t('Falta')]) });
  const pres = f('presente');
  if (tem('presentes') && pres.length) b.push({ t: 'sub', texto: t('Presentes') },
    { t: 'tabela', colunas: [t('Quem deu'), t('O quê'), t('Agradeci')], larguras: [0.34, 0.46, 0.2], linhas: pres.map(r => [r.c.quem || '', r.c.oque || '', r.c.agradeci ? t('Sim') : t('Não')]) });
  const cotas = f('cota');
  if (tem('vaquinha') && cotas.length && pessoasDoEvento(ev).length > 1) b.push({ t: 'sub', texto: t('Vaquinha') },
    { t: 'tabela', colunas: [t('Pessoa'), t('Vai contribuir'), t('Já deu'), t('Falta')], larguras: [0.34, 0.22, 0.22, 0.22],
      linhas: cotas.map(r => { const m = r.c.moedas?.valor || ctx.moeda; return [r.c.pessoa || '', din(num(r.c.valor), m), din(num(r.c.deu), m), din(Math.max(0, num(r.c.valor) - num(r.c.deu)), m)]; }) });
  if (tem('notas') && String(ev.c.notas ?? '').trim()) b.push({ t: 'sub', texto: t('Anotações') }, { t: 'par', texto: String(ev.c.notas).trim() });
  marcarAbaAtual('eventos');   // o PDF entra no histórico de relatórios da aba
  await gerarECompartilharPdf(ev.c.nome || t('Evento'), b, 'My Planner');
}

// Janela do relatório (uma só, aberta pelo ⋮ do evento ou pela sub-aba Orçamento e gastos)
let relAberto: string | null = null;
const ouvRel = new Set<() => void>();
export function abrirRelatorioEvento(id: string | null) { relAberto = id; ouvRel.forEach(f => f()); }
const useRelAberto = () => useSyncExternalStore(cb => { ouvRel.add(cb); return () => { ouvRel.delete(cb); }; }, () => relAberto, () => relAberto);
function RelatorioEvento({ ctx }: { ctx: Ctx }) {
  useMembrosViagem();
  const id = useRelAberto();
  const ev = id ? ctx.porId(id) : null;
  const [sel, setSel] = useState<ParteRel[]>(PADRAO_REL);
  const [tel, setTel] = useState(false);
  const [gerando, setGerando] = useState(false);
  const fechar = () => abrirRelatorioEvento(null);
  const gerar = async () => {
    if (!ev || gerando) return;
    setGerando(true);
    try { await semBloqueio(() => relatorioEventoPdf(ctx, ev, sel, tel)); }
    catch (e: any) { Alert.alert(t('Não foi possível gerar o PDF.'), String(e?.message ?? e)); }
    finally { setGerando(false); }
  };
  const caixa = (on: boolean, rotulo: string, alternar: () => void, k: string) => (
    <Pressable key={k} onPress={alternar} accessibilityRole="checkbox" accessibilityState={{ checked: on }} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
      <Text style={{ fontSize: 20, color: on ? ctx.cor : C.sub }}>{on ? '☑' : '☐'}</Text>
      <Text style={{ color: C.text, flex: 1 }}>{rotulo}</Text>
    </Pressable>);
  return (
    <Sheet visible={!!ev} onClose={fechar} title={t('Relatório do evento')} larga>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        {!!ev && <CartaoTotal valor={din(numerosEvento(ctx, ev).gasto, ctx.moeda)} legenda={t('gasto em {e}', { e: ev.c.nome || t('Evento') })} />}
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('O QUE INCLUIR')}</Text>
        {PARTES_REL.filter(([k]) => k !== 'vaquinha' || pessoasDoEvento(ev).length > 1).map(([k, nome]) => caixa(sel.includes(k), t(nome), () => setSel(l => (l.includes(k) ? l.filter(y => y !== k) : [...l, k])), k))}
        {sel.includes('convidados') && caixa(tel, t('Incluir telefones'), () => setTel(v => !v), 'tel')}
        <Btn cor={ctx.cor} outline disabled={gerando || !sel.length} label={'📄 ' + (gerando ? t('Gerando…') : t('Exportar em PDF'))} onPress={gerar} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('Os telefones dos convidados só saem no PDF se você marcar "Incluir telefones". Valores em moedas diferentes aparecem separados.')}</Text>
        <RelatoriosGerados aba="eventos" cor={ctx.cor} />
      </ScrollView>
    </Sheet>
  );
}

